import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Wallet } from "lucide-react";
import { Button } from "@veorest/ui";
import {
  adminWalletSetLimits,
  adminWalletTopup,
  fetchAdminWallet,
  type AdminWalletType,
} from "@veorest/api-client";
import { formatIqd } from "../../lib/format";

function formatWhen(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const TX_LABELS: Record<string, string> = {
  topup: "Top-up",
  platform_fee: "Platform fee",
  adjustment: "Adjustment",
  refund: "Refund",
};

/**
 * Multi-wallet v2 management card (S1).
 * Wallet purpose is FEE-ONLY: per-trip platform fee (Settings → Plans Settings),
 * + delivery settlements. Food / order values never touch this wallet.
 */
export function AdminWalletCard({
  walletType,
  ownerId,
  title,
  subtitle,
  className,
  managedByPartner = false,
}: {
  walletType: AdminWalletType;
  ownerId: string;
  title?: string;
  subtitle?: string;
  className?: string;
  /** Partner-managed client — top-ups/limits live on the partner wallet. */
  managedByPartner?: boolean;
}) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["admin-wallet", walletType, ownerId],
    enabled: Boolean(ownerId),
    queryFn: () => fetchAdminWallet(walletType, ownerId),
  });

  const [topupAmount, setTopupAmount] = useState("");
  const [topupNote, setTopupNote] = useState("");
  const [limitValue, setLimitValue] = useState("");
  const [thresholdValue, setThresholdValue] = useState("");
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  useEffect(() => {
    if (!query.data || loadedFor === ownerId) return;
    setLimitValue(String(query.data.negative_credit_limit));
    setThresholdValue(String(query.data.alert_threshold_iqd));
    setLoadedFor(ownerId);
  }, [query.data, ownerId, loadedFor]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin-wallet", walletType, ownerId] });
  };

  const doTopup = useMutation({
    mutationFn: () => adminWalletTopup(walletType, ownerId, Number(topupAmount), topupNote),
    onSuccess: (snap) => {
      toast.success(`Top-up applied — balance ${formatIqd(snap.balance)}`);
      setTopupAmount("");
      setTopupNote("");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const doSetLimits = useMutation({
    mutationFn: () => {
      const limit = Number(limitValue);
      if (!Number.isFinite(limit) || limit > 0) {
        throw new Error("Negative credit limit must be 0 or lower (e.g. -50000).");
      }
      const threshold = thresholdValue.trim() === "" ? undefined : Number(thresholdValue);
      if (threshold !== undefined && (!Number.isFinite(threshold) || threshold < 0)) {
        throw new Error("Alert threshold must be 0 or higher.");
      }
      return adminWalletSetLimits(walletType, ownerId, limit, threshold);
    },
    onSuccess: (snap) => {
      toast.success(`Limits saved — credit limit ${formatIqd(snap.negative_credit_limit)}`);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const wallet = query.data;
  const blocked = wallet ? wallet.balance < wallet.negative_credit_limit : false;
  const low = wallet ? wallet.balance <= wallet.alert_threshold_iqd : false;

  return (
    <section className={`overview-panel overview-panel--fit ${className ?? ""}`}>
      <header className="overview-panel__header">
        <span className="overview-panel__icon" aria-hidden>
          <Wallet className="h-4 w-4" />
        </span>
        <div>
          <h3 className="overview-panel__title">{title ?? "OELOX wallet"}</h3>
          <p className="overview-panel__subtitle">
            {subtitle ?? "Fee-only rail — platform fees and settlements."}
          </p>
        </div>
        {wallet ? (
          <span
            className={`ml-auto rounded-full px-3 py-1 text-xs font-bold ${
              blocked
                ? "bg-red-50 text-red-600"
                : low
                  ? "bg-amber-50 text-amber-700"
                  : "bg-green-50 text-green-700"
            }`}
          >
            {blocked ? "Dispatch blocked" : low ? "Low balance" : "Healthy"}
          </span>
        ) : null}
      </header>
      <div className="overview-panel__body space-y-4 p-5">
        {query.isLoading ? (
          <p className="text-sm font-semibold text-[var(--text-muted)]">Loading wallet…</p>
        ) : query.isError ? (
          <p className="text-sm font-semibold text-red-500">
            Could not load wallet: {(query.error as Error)?.message}
          </p>
        ) : !wallet ? (
          <p className="text-sm font-semibold text-[var(--text-muted)]">No wallet data.</p>
        ) : (
          <>
            <div className="sa-wallet-stats">
              <div
                className={`sa-wallet-stat ${wallet.balance < 0 ? "sa-wallet-stat--danger" : ""}`}
              >
                <p className="sa-wallet-stat__label">Balance</p>
                <p className="sa-wallet-stat__value">{formatIqd(wallet.balance)}</p>
                <p className="sa-wallet-stat__caption">
                  {blocked ? "Dispatch blocked" : low ? "Low balance" : "Healthy · fee dues"}
                </p>
              </div>
              <div className="sa-wallet-stat">
                <p className="sa-wallet-stat__label">Credit limit</p>
                <p className="sa-wallet-stat__value">
                  {formatIqd(wallet.negative_credit_limit)}
                </p>
                <p className="sa-wallet-stat__caption">Dispatch blocks below this</p>
              </div>
              <div className="sa-wallet-stat">
                <p className="sa-wallet-stat__label">Alert at</p>
                <p className="sa-wallet-stat__value">
                  {formatIqd(wallet.alert_threshold_iqd)}
                </p>
                <p className="sa-wallet-stat__caption">Push alert threshold</p>
              </div>
            </div>

            {wallet.totals.tx_count > 0 ? (
              <p className="text-xs font-semibold text-[var(--text-muted)]">
                To date: {wallet.totals.tx_count} entries · platform fees{" "}
                {formatIqd(Math.abs(wallet.totals.fees_total))} · top-ups{" "}
                {formatIqd(wallet.totals.topups_total)}
              </p>
            ) : null}

            {managedByPartner ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-800">
                Partner-managed — top-ups and limits live on the partner wallet.
              </div>
            ) : (
            <div className="sa-wallet-controls">
              <div className="sa-wallet-controls__group">
                <span className="sa-wallet-controls__title">Top up</span>
                <label className="block space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">
                    Amount (IQD)
                  </span>
                  <input
                    className="input-base w-32"
                    inputMode="decimal"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">
                    Note
                  </span>
                  <input
                    className="input-base w-44"
                    value={topupNote}
                    onChange={(e) => setTopupNote(e.target.value)}
                    placeholder="Optional reference"
                  />
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-[40px] sa-soft sa-soft--success"
                  loading={doTopup.isPending}
                  disabled={!topupAmount || Number(topupAmount) <= 0}
                  onClick={() => doTopup.mutate()}
                >
                  Apply top-up
                </Button>
              </div>
              <div className="sa-wallet-controls__group">
                <span className="sa-wallet-controls__title">Credit limit & alert</span>
                <label className="block space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">
                    Limit (≤ 0)
                  </span>
                  <input
                    className="input-base w-32"
                    inputMode="decimal"
                    value={limitValue}
                    onChange={(e) => setLimitValue(e.target.value)}
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-xs font-bold uppercase text-[var(--text-muted)]">
                    Alert (IQD)
                  </span>
                  <input
                    className="input-base w-32"
                    inputMode="decimal"
                    value={thresholdValue}
                    onChange={(e) => setThresholdValue(e.target.value)}
                  />
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-[40px] sa-soft sa-soft--accent"
                  loading={doSetLimits.isPending}
                  disabled={limitValue.trim() === ""}
                  onClick={() => doSetLimits.mutate()}
                >
                  Save limits
                </Button>
              </div>
            </div>
            )}

            {wallet.transactions.length === 0 ? (
              <p className="text-sm font-semibold text-[var(--text-muted)]">
                No wallet transactions yet.
              </p>
            ) : (
              <div className="admin-table-wrap">
                <table className="table-flat sa-ledger">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th className="text-end">Amount</th>
                      <th className="text-end">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wallet.transactions.slice(0, 20).map((entry) => (
                      <tr key={entry.id}>
                        <td className="whitespace-nowrap text-xs font-semibold text-[var(--text-muted)]">
                          {formatWhen(entry.created_at)}
                        </td>
                        <td>
                          <span
                            className={`sa-type-chip sa-type-chip--${
                              TX_LABELS[entry.type] ? entry.type : "default"
                            }`}
                          >
                            {TX_LABELS[entry.type] ?? entry.type}
                          </span>
                        </td>
                        <td
                          className="sa-ledger__desc text-sm text-[var(--text-muted)]"
                          title={entry.description || undefined}
                        >
                          {entry.description || "—"}
                        </td>
                        <td className="whitespace-nowrap text-end font-bold tabular-nums">
                          <span
                            className={
                              entry.amount < 0 ? "sa-ledger__amount--out" : "sa-ledger__amount--in"
                            }
                          >
                            {formatIqd(entry.amount)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap text-end font-bold tabular-nums">
                          {formatIqd(entry.balance_after)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
